import React, { useState, useEffect, useRef } from "react";
import PuzzleBoard from "./PuzzleBoard";
import PuzzlePiece from "./PuzzlePiece";          // ✅ import from external file (uses "PIECE")
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import MessageModal from "../MessageModal";
import * as SC from "../GameScreen/GameScreen.style";
import { ReactComponent as StarsSvg } from "../../assets/stars.svg";
import { IconImage } from "../MasterCss";
import ProgressScore from "../GameScreen/ProgressScore";
import HowToPlayModal from "../LandingScreen/HowToPlayModal";
import OptionsModal from "../GameScreen/OptionsModal";
import LivesScore from "../GameScreen/LivesScore";
import { ReactComponent as SpaceOctopus } from "../../assets/space-octopus.svg";
import { spinscale } from "../Keyframes";
import PauseIcon from "../../assets/pause-icon.svg";
import LogoImg from "../../assets/crab.png";
import { useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";

const Stars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 120vw;
  height: 100vh;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-around;
  overflow: hidden;
  animation: ${spinscale} 4s linear infinite alternate;
`;

const PuzzleBoardContainer = styled.div`
  display: flex;
  justify-content: center;
  background-image: linear-gradient(#060606, #08345c);
  align-items: center;
  width: 100%;
  padding: 20px;
  width: 100vw;
  z-index: -10;
  height: 100vh;

  @media (max-width: 768px) {
    max-width: 90%;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    padding: 10px;
  }
`;

const LogoStatic = styled.img`
  position: fixed;
  bottom: 5%;
  right: 5%;
  width: 10vw;
  height: auto;
  z-index: 5;

  @media (max-width: 768px) {
    width: 15vw;
    bottom: 5%;
    right: 5%;
  }

  @media (max-width: 480px) {
    width: 20vw;
    bottom: 5%;
    right: 5%;
  }
`;

const Octopus = styled.div`
  position: absolute;
  bottom: 10px;
  overflow: hidden;
  left: 10px;
  animation: ${spinscale} 8s linear infinite alternate;
`;

const PuzzleBoardScreen = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const restart = location.state?.restart;
  const hasShownGuide = useRef(false);

  const puzzles = props.puzzleImages || [];
  const [currentPuzzleIndex, setCurrentPuzzleIndex] = useState(
    puzzles.length > 0 ? 0 : -1
  );
  const [imagePieces, setImagePieces] = useState([]);
  const [placedPieces, setPlacedPieces] = useState([]); // ✅ now an array
  const [message, setMessage] = useState(null);
  const [successModal, setSuccessModal] = useState(false);
  const [optionsModal, setOptionsModal] = useState(false);
  const [failModal, setFailModal] = useState(false);
  const [isMobileScreen] = useState(
    window.matchMedia("screen and (max-width: 768px)").matches
  );
  const currentImageSrcRef = useRef(null);

  // Redirect if no puzzles
  useEffect(() => {
    if (puzzles.length === 0) {
      navigate(`/results?game=waste-puzzle`);
    }
  }, [puzzles, navigate]);

  // Load puzzle when index changes
  useEffect(() => {
    if (puzzles.length > 0 && currentPuzzleIndex >= 0) {
      const imgSrc = puzzles[currentPuzzleIndex];
      if (imgSrc !== currentImageSrcRef.current) {
        loadPuzzle(imgSrc);
        currentImageSrcRef.current = imgSrc;
      }
    }
  }, [currentPuzzleIndex, puzzles]);

  // Show how-to-play only once
  useEffect(() => {
    if (!hasShownGuide.current && !restart) {
      props.showHowToPlayModal();
      hasShownGuide.current = true;
    } else {
      hasShownGuide.current = true;
    }
  }, [props.showHowToPlayModal, restart]);

  // ---------- Puzzle logic ----------
  const loadPuzzle = (imgSrc) => {
    if (!imgSrc) return;
    setImagePieces([]);
    setPlacedPieces(Array(9).fill(null));  // ✅ 3x3 grid → 9 slots
    setMessage(null);

    const img = new Image();
    img.src = imgSrc;
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (img.width && img.height) {
        splitImage(img, 3, 3);
      } else {
        console.error("Image loaded but width/height is zero");
      }
    };
    img.onerror = () => console.error("Failed to load image:", imgSrc);
  };

  const splitImage = (img, rows, cols) => {
    const pieceWidth = img.width / cols;
    const pieceHeight = img.height / rows;
    const pieces = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const canvas = document.createElement("canvas");
        canvas.width = pieceWidth;
        canvas.height = pieceHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(
          img,
          x * pieceWidth,
          y * pieceHeight,
          pieceWidth,
          pieceHeight,
          0,
          0,
          pieceWidth,
          pieceHeight
        );
        pieces.push({ id: `${x}-${y}`, src: canvas.toDataURL(), x, y });
      }
    }
    setImagePieces(shuffleArray(pieces));
  };

  const shuffleArray = (array) => array.sort(() => Math.random() - 0.5);

  // ----- Drop handlers (array version) -----
  const handleDrop = (piece, targetId) => {
    // targetId is the slot index (0–8)
    setPlacedPieces((prev) => {
      const newPlaced = [...prev];
      const existing = newPlaced[targetId];
      if (existing) {
        // Return existing piece to source
        setImagePieces((srcPrev) => {
          if (!srcPrev.some(p => p.id === existing.id)) {
            return [...srcPrev, existing];
          }
          return srcPrev;
        });
      }
      newPlaced[targetId] = piece;
      return newPlaced;
    });

    // Remove dropped piece from source
    setImagePieces((prev) => prev.filter((p) => p.id !== piece.id));
  };

  const handleDropToSource = (piece) => {
    // Remove piece from any slot it occupies
    setPlacedPieces((prev) => {
      const newPlaced = [...prev];
      const slotIndex = newPlaced.findIndex(p => p && p.id === piece.id);
      if (slotIndex !== -1) {
        newPlaced[slotIndex] = null;
      }
      return newPlaced;
    });

    // Add back to source if not already there
    setImagePieces((prev) => {
      if (!prev.some(p => p.id === piece.id)) {
        return [...prev, piece];
      }
      return prev;
    });
  };

  // ✅ NEW: remove piece by clicking on it (or on the X button)
  const handleRemovePiece = (targetId, piece) => {
    setPlacedPieces((prev) => {
      const newPlaced = [...prev];
      newPlaced[targetId] = null;
      return newPlaced;
    });
    setImagePieces((prev) => {
      if (!prev.some(p => p.id === piece.id)) {
        return [...prev, piece];
      }
      return prev;
    });
  };

  // ----- Completion check -----
  const checkIfPuzzleCompleted = () => {
    const allSlotsFilled = placedPieces.every(p => p !== null);
    if (!allSlotsFilled) return;

    const isCorrect = placedPieces.every((piece, index) => {
      const targetX = index % 3;
      const targetY = Math.floor(index / 3);
      return piece.x === targetX && piece.y === targetY;
    });

    if (isCorrect) {
      setMessage({
        text: t('puzzle.successMessage'),
        type: "success",
      });
      props.setCount(props.count + 2);
      setTimeout(() => handleNextPuzzle(), 100);
    } else {
      setMessage({
        text: t('puzzle.errorMessage'),
        type: "error",
      });
      props.setBadCount(props.badCount + 1);
    }
  };

  useEffect(() => {
    // Only check if all slots are filled (length 9)
    if (placedPieces.length === 9 && placedPieces.every(p => p !== null)) {
      checkIfPuzzleCompleted();
    }
  }, [placedPieces]);

  useEffect(() => {
    if (props.badCount === 3) {
      setFailModal(true);
      setTimeout(() => navigate(`/results?game=waste-puzzle`), 500);
    }
  }, [props.badCount, navigate]);

  const handleNextPuzzle = () => {
    if (currentPuzzleIndex + 1 >= puzzles.length) {
      navigate(`/results?game=waste-puzzle`);
    } else {
      setCurrentPuzzleIndex((prev) => prev + 1);
      setPlacedPieces(Array(9).fill(null));
    }
  };

  const closeMessageModal = () => {
    setMessage(null);
    if (puzzles[currentPuzzleIndex]) {
      loadPuzzle(puzzles[currentPuzzleIndex]);
    }
  };

  const showOptionsModal = () => {
    setOptionsModal(!optionsModal);
  };

  // ----- Render -----
  return (
    <PuzzleBoardContainer>
      {optionsModal && (
        <OptionsModal
          optionsModal={optionsModal}
          setOptionsModal={setOptionsModal}
          setCount={props.setCount}
          setGame={props.setGame}
          setBadCount={props.setBadCount}
          funFactsModal={props.funFactsModal}
          setFunFactsModal={props.setFunFactsModal}
          howToPlayModal={props.howToPlayModal}
          setHowToPlayModal={props.setHowToPlayModal}
          showFunFactsModal={props.showFunFactsModal}
          hideFunFactsModal={props.hideFunFactsModal}
          hideHowToPlayModal={props.hideHowToPlayModal}
          showHowToPlayModal={props.showHowToPlayModal}
        />
      )}

      {props.howToPlayModal && (
        <HowToPlayModal
          handleClose={props.hideHowToPlayModal}
          game={"waste-puzzle"}
        />
      )}

      <SC.Header>
        <IconImage
          game
          onClick={showOptionsModal}
          src={PauseIcon}
          cursor="pointer"
        />
        <SC.LivesContainer>
          <LivesScore
            badCount={props.badCount}
            successModal={successModal}
            failModal={failModal}
          />
        </SC.LivesContainer>
        <ProgressScore gameScreen count={props.count} />
      </SC.Header>

      {message && (
        <MessageModal
          type={message.type}
          message={message.text}
          onClose={closeMessageModal}
        />
      )}

      <Octopus>{!isMobileScreen && <SpaceOctopus />}</Octopus>
      <Stars>
        <StarsSvg />
      </Stars>

      <PuzzleBoard
        pieces={imagePieces}
        rows={3}
        cols={3}
        placedPieces={placedPieces}          // ✅ now an array
        onDrop={handleDrop}
        onDropToSource={handleDropToSource}
        onRemovePiece={handleRemovePiece}    // ✅ passed down
      />

      <LogoStatic src={LogoImg} alt="Mascot" />
    </PuzzleBoardContainer>
  );
};

export default PuzzleBoardScreen;